import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listShipments, type Shipment } from '../../api/shipping'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterBar } from '@/components/FilterBar'
import { PageHeader } from '@/components/PageHeader'
import { StatusBadge } from '@/components/StatusBadge'

const SHIPPING_FILTERS = [
  { id: 'alle', label: 'Alle' },
  { id: 'ausstehend', label: 'Ausstehend' },
  { id: 'versendet', label: 'Versendet' },
  { id: 'zugestellt', label: 'Zugestellt' },
  { id: 'retoure', label: 'Retoure' },
]

export default function ShippingList() {
  const navigate = useNavigate()
  const [activeFilter, setActiveFilter] = useState('alle')
  const [search, setSearch] = useState('')
  const [minId, setMinId] = useState<number | undefined>()
  const [pageHistory, setPageHistory] = useState<number[]>([])

  function handleFilterChange(id: string) {
    setActiveFilter(id)
    setMinId(undefined)
    setPageHistory([])
  }

  function handleSearch(value: string) {
    setSearch(value)
    setMinId(undefined)
    setPageHistory([])
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['shipments', activeFilter, search, minId],
    queryFn: () =>
      listShipments({
        status: activeFilter !== 'alle' ? activeFilter : undefined,
        search: search || undefined,
        min_id: minId,
      }),
    staleTime: 30_000,
  })

  const columns: Column<Shipment>[] = [
    {
      key: 'order_number',
      header: 'Auftrag',
      cell: (s) => s.order_number ?? `#${s.order_id}`,
    },
    { key: 'deliverer', header: 'Dienstleister' },
    {
      key: 'tracking_code',
      header: 'Sendungsnummer',
      className: 'font-mono text-xs',
      cell: (s) => s.tracking_code ?? '—',
    },
    {
      key: 'shipped_at',
      header: 'Datum',
      cell: (s) => s.shipped_at ? new Date(s.shipped_at).toLocaleDateString('de-DE') : '—',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (s) => <StatusBadge status={s.status} />,
    },
  ]

  return (
    <div className="p-6">
      <PageHeader title="Versand" />
      <FilterBar
        filters={SHIPPING_FILTERS}
        active={activeFilter}
        onChange={handleFilterChange}
        search={search}
        onSearch={handleSearch}
        placeholder="Sendungsnummer, Auftrag…"
      />
      <div className="mt-4">
        {isError && (
          <p className="text-sm text-destructive mb-3">Fehler beim Laden.</p>
        )}
        <DataTable
          data={data?.shipments ?? []}
          columns={columns}
          rowKey={(s) => s.id}
          onRowClick={(s) => navigate(`/shipping/${s.id}`)}
          isLoading={isLoading}
          emptyMessage="Keine Sendungen gefunden."
          pagination={data ? {
            hasMore: data.has_more,
            onNext: () => {
              if (data.next_min_id) {
                setPageHistory((h) => [...h, minId ?? 0])
                setMinId(data.next_min_id)
              }
            },
            onPrev: () => {
              const prev = pageHistory[pageHistory.length - 1]
              setPageHistory((h) => h.slice(0, -1))
              setMinId(prev === 0 ? undefined : prev)
            },
            canPrev: pageHistory.length > 0,
            count: data.shipments.length,
          } : undefined}
        />
      </div>
    </div>
  )
}
